import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import Konva from 'konva';
//import { Howl } from 'howler';
import { Stage, Layer, Rect, Group, Line, Text } from 'react-konva';
import { getSoundPropsById, isNotEmpty } from '../../lib/ep.js';
import { useImageLoader } from '../../lib/hooks.js';

import Spinner from '../builder/Spinner.js';

const item_types = ['ok', 'ko', 'superok', 'superko'];

const standard_board_size = 600;	// px

const default_catch_effects = {
	superok:{
		sound_fx:'success',
	},
	ok:{
		sound_fx:'hint',
	},
	ko:{
		sound_fx:'error_buzzer',
	},
	superko:{
		sound_fx:'funny_laugh',
	},
};

const getEffects = (stage_effects) => {
	const effects = {};
	for(let type in default_catch_effects) {
		effects[type] = { ...default_catch_effects[type] };

		if(stage_effects[type] == undefined) continue;
		effects[type] = { ...effects[type], ...stage_effects[type] };
	}

	return effects;
};

const CatchItCanvas = forwardRef((props, ref) => {
	const [ indexed_items, setIndexedItems ] = useState({});
	const [ full_loaded, setFullLoaded ] = useState(false);
	const timer = useRef(null);

	const is_debug = false;

	const stage = props?.stage ?? {};
	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;
	const is_playable = props?.is_playable ?? ((!is_builder || is_test) ? true : false);
	const level_increase_delay = 1.5;			// in sec
	const speed_up_factor = 0.95;			// in % from 0-1
	const min_delay_between_items = 2;		// in sec

	const ok_color = props?.ok_color ?? 'green';
	const ko_color = props?.ko_color ?? 'red';

	const rotate_on_fall = stage?.catch_rotate_on_fall ?? true;
	const display_outter_border = stage?.display_outter_border ?? true;

	let speed_multiplier = 1;
	switch(stage?.catch_speed ?? 'normal') {
		case 'inferno':
			speed_multiplier = 1.2;
			break;

		case 'fast':
			speed_multiplier = 1.6;
			break;

		case 'normal':
			speed_multiplier = 2;
			break;

		case 'slow':
			speed_multiplier = 2.4;
			break;
	}

	let items_generation_multiplier = 1;
	switch(stage?.catch_num_items ?? 'normal') {
		case 'less':
			items_generation_multiplier = 2;
			break;

		case 'normal':
			items_generation_multiplier = 4;
			break;

		case 'more':
			items_generation_multiplier = 8;
			break;

		case 'inferno':
			items_generation_multiplier = 12;
			break;
	}

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const game_score = useRef(0);

	const effects = getEffects(stage?.catch_effects ?? {});

	const points_for_ok = 10;			// positive
	const points_for_superok = 50;		// positive
	const points_for_ko = 50;			// negative
	const points_for_miss = 5;			// negative
	const points_for_superko = 5000;	// negative

	// the probability to get a OK item equals to "1 - superok_probability - superko_probability - ko_probability":
	const superok_probability = 0.1;		// in % from 0-1
	const superko_probability = 0.1;		// in % from 0-1
	const ko_probability = 0.3;				// in % from 0-1
	const delay_to_success = 0.5; // in seconds

	const size = useRef(standard_board_size);
	const min_detection_y = 0.75 * size.current;	// for optimization reasons, we only start detecting collisions once passed this vertical point

	const player_size = {
		w:size.current / 5,
		h:size.current / 10,
	};

	const item_size = {
		w:60,
		h:60,
	};

	const refs = useRef([]);
	const falling_items = useRef(0);
	const catched_items = useRef(0);
	const delay_next = useRef(3);	// sec
	const stoppedGame = useRef(false);
	const animations = useRef(false);
	const game_ended = useRef(false);
	const detector = useRef(false);

	/* SOUNDS - ini */
	const sound_ok = (effects.ok.sound_fx != 'none') ? new Howl({ ...getSoundPropsById(effects.ok.sound_fx) }) : false;
	const soundOK = () => {
		if(sound_ok === false) return;
		sound_ok.play();
	}

	const sound_ko = (effects.ko.sound_fx != 'none') ? new Howl({ ...getSoundPropsById(effects.ko.sound_fx) }) : false;
	const soundKO = () => {
		if(sound_ko === false) return;
		sound_ko.play();
	};

	const sound_superok = (effects.superok.sound_fx != 'none') ? new Howl({ ...getSoundPropsById(effects.superok.sound_fx) }) : false;
	const soundSuperOK = () => {
		if(sound_superok === false) return;
		sound_superok.play();
	};

	const sound_superko = (effects.superko.sound_fx != 'none') ? new Howl({ ...getSoundPropsById(effects.superko.sound_fx) }) : false;
	const soundSuperKO = () => {
		if(sound_superko === false) return;
		sound_superko.play();
	};
	/* SOUNDS - end */

	const prepareImagesToLoad = () => {
		const images = [];

		if(isNotEmpty(stage, 'catch_bg_image')) {
			images.push({
				id:'full_bg',
				name:'full_bg',
				url:stage.full_catch_bg_image,
				use_full_size:true,
			});
		}

		if(isNotEmpty(stage, 'catch_player_image')) {
			images.push({
				id:'full_player',
				name:'full_player',
				url:stage.full_catch_player_image,
				fit:'contain',
			});
		}

		(stage?.catch_images ?? []).map((image) => {
			if(!isNotEmpty(image, 'image')) return;

			images.push({
				id:image.type,
				name:image.image,
				url:image.full_image,
				image:image.full_image,
				fit:'contain',
			});
		});

		return images;
	};

	const images = useMemo(prepareImagesToLoad, [
		stage.catch_images,
		stage.full_catch_bg_image,
		stage.full_catch_player_image,
	]);

	useImageLoader(images, (loaded_images) => {
		const current = {};
		setFullLoaded(false);

		Object.keys(loaded_images).map((image_name) => {
			const image = loaded_images[image_name];

			if(!isNotEmpty(image, 'img')) return;

			const type = image.id;
			const new_image = adaptImageSizes({
				fillPatternImage:image.img,
				fit:image.fit,
				image_w:image.w,
				image_h:image.h,
				use_full_size:image?.use_full_size ?? false,
			});
			if(type == 'foreground') {
				item_h_proportion.current = 1 - new_image.scale * image.h / item_size.h;
				max_y_to_hit.current = item_h_proportion.current * item_size.h - 0.1;
			}

			if(item_types.includes(type)) {
				if(current[type] == undefined) current[type] = [];
				current[type] = [...current[type],new_image];
				return;
			}

			current[type] = new_image;
		})

		setIndexedItems(current);

		setTimeout(() => {
			gameInit();
		}, 0);
	});

	const calcImageSize = (w, h, type = 'cover', use_full_size = false) => {
		const w_prop = (((!use_full_size) ? item_size.w : size.current) + 1) / w;
		const h_prop = (((!use_full_size) ? item_size.h : size.current) + 1) / h;

		if(type == 'contain') return Math.min(w_prop, h_prop);

		return Math.max(w_prop, h_prop);
	};

	const calcItemOffset = (item, _size) => {
		let offset_x = (_size * item.w - item_size.w) / 2;
		let offset_y = (_size * item.h - item_size.h) / 2;

		return { x:offset_x, y:offset_y };
	};

	const adaptImageSizes = (image) => {
		const _scale = calcImageSize(image.image_w, image.image_h, image.fit, image.use_full_size);
		const _offset = calcItemOffset({ w:image.image_w, h:image.image_h }, _scale);
	
		return { ...image, scale:_scale, offset:_offset };
	};
 
	const exitGame = () => {
		return;

		if(refs.current['main_stage']) {
			//killAllAnimations();

			animations.current = false;
			refs.current['main_stage'].removeChildren();
			refs.current['main_stage'].destroy();
		}
	};

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};

	useEffect(() => {
		if(!full_loaded) return;

		if(refs.current['layer'] != undefined) {
			refs.current['player_image'].cache(default_cache_props);

			refs.current['layer'].draw();

			if(is_playable) {
				setTimeout(() => {
					startAnimations();
				}, 0);
			}
		}

		return () => {
			clearTimeout(timer.current);
		}
	}, [ full_loaded ]);

	useEffect(() => {
		if(!is_builder) window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);
			exitGame();

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);
		}
	}, []);

	useImperativeHandle(ref, () => ({
		togglePlay() {
			if(!is_builder) return;

			if(animations.current !== false) {
				animations.current = false;
				detector.current = false;

				game_score.current = 0;
				if(props.onScore != undefined) props.onScore(0);
				return;
			}

			setTimeout(() => {
				startAnimations();
			}, 0);
		},

		stopGame(success_game, cb,reason) {
			stoppedGame.current = true;
			animations.current = false;
			detector.current = false;

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			const cleanup = () => {
				exitGame();

				if(cb != undefined) {
					setTimeout(() => {
						cb(reason == 'time_up' ?? success_game);
					}, (delay_to_success * 1000));
				}
			}
			cleanup();
		},
	}), [ indexed_items ]);

	const gameInit = () => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((size_received) => {
				size.current = size_received;
				setFullLoaded(true);

				props.startTimer();
			});
		} else {
			setFullLoaded(true);
		}
	};

	const haveIntersection = (r1, r2) => {
		return !(
			r2.x > r1.x + r1.width ||
			r2.x + r2.width < r1.x ||
			r2.y > r1.y + r1.height ||
			r2.y + r2.height < r1.y
		);
	};

	const startAnimations = () => {
		stoppedGame.current = false;
		game_ended.current = false;
		animations.current = true;
		delay_next.current = 3;

		setTimeout(() => {
			addFallingItem('ok', 0);

			timer.current = setInterval(() => {
				if(animations.current === false) return;

				addFallingItem('ok', 0);
			}, level_increase_delay * 1000);
		}, 0);

		detector.current = window.requestAnimationFrame(detectCollisions);
	};

	const addScoreLabel = (score, text_x) => {
		const label_w = size.current / 4;
		const label_h = 30;
		const text_id = catched_items.current;

		const text_props = {
			text:(score > 0) ? `+${score}` : score,
			width:label_w,
			height:label_h,
			textAlign:'center',
			verticalAlign:'middle',
			fontStyle:'bold',
			fontSize:20,
		};

		const text_shadow = new Konva.Text({
			...text_props,
			stroke:'white',
			strokeWidth:4,
		});

		const text = new Konva.Text({
			...text_props,
			fill:(score > 0) ? ok_color : ko_color,
		});

		const text_group = new Konva.Group({
			x:text_x - 5,
			y:size.current - player_size.h,
			offsetX:label_w / 2,
			offsetX:label_h / 2,
			opacity:0,
		});

		refs.current[`score_label_${text_id}`] = text_group;

		text_group.add(text_shadow);
		text_group.add(text);

		refs.current['score_labels'].add(text_group);

		refs.current[`score_label_${text_id}`].to({
			y:size.current - player_size.h - 60,
			duration:1,
			easing:Konva.Easings.StrongEaseOut,
		});

		refs.current[`score_label_${text_id}`].to({
			opacity:1,
			duration:0.2,
			onFinish:() => {
				setTimeout(() => {
					text.to({
						opacity:1,
						duration:0,
						onFinish:() => {
							refs.current[`score_label_${text_id}`].destroy();
						},
					});
				}, (0.6 * 1000));
			}
		})
	};

	const updateScore = (type, text_x = false) => {
		if(animations.current == false) return;

		let new_score = 0;
		switch(type) {
			case 'ok':
				new_score = Math.round(points_for_ok);
				break;

			case 'superok':
				new_score = Math.round(points_for_superok);
				break;

			case 'ko':
				new_score = -points_for_ko;
				break;

			case 'superko':
				new_score = -points_for_superko;
				break;

			case 'miss':
			default:
				new_score = -points_for_miss;
				break;
		}

		if(text_x !== false) {
			catched_items.current++;
			addScoreLabel(new_score, text_x);
		}

		game_score.current = Math.max(0, game_score.current + new_score);

		if(props.onScore != undefined) props.onScore(game_score.current)
	};

	const setItemType = () => {
		const seed = Math.random();
		if((indexed_items?.ko ?? []).length > 0) {
			if(seed <= (ko_probability)) return 'ko';

			if((indexed_items?.superko ?? []).length > 0 && seed <= (ko_probability + superko_probability)) return 'superko';
			if((indexed_items?.superok ?? []).length > 0 && seed <= (ko_probability + superko_probability + superok_probability)) return 'superok';
		} else {
			if((indexed_items?.superko ?? []).length > 0 && seed <= (superko_probability)) return 'superko';
			if((indexed_items?.superok ?? []).length > 0 && seed <= (superko_probability + superok_probability)) return 'superok';
		}

		return 'ok';
	};

	const addFallingItem = (type, index = 0) => {
		if(refs.current['main_stage'] == undefined) return;
		if(animations.current === false) return;

		falling_items.current++;
		
		const number = `item_${falling_items.current}`;

		const item_margin = 1.5 * player_size.w;

		const item = new Konva.Rect({
			width:item_size.w,
			height:item_size.h,
			offsetX:item_size.w / 2,
			offsetY:item_size.h / 2,
			x:Math.random() * (size.current - item_margin) + 0.5 * item_margin,
			fillPatternImage:indexed_items[type][index].fillPatternImage,
			fillPatternRepeat:'no-repeat',
			fillPatternScaleX:indexed_items[type][index]?.scale ?? null,
			fillPatternScaleY:indexed_items[type][index]?.scale ?? null,
			y:-item_size.h,
			type,
			opacity:(is_debug) ? 0.2 : 1,
		});

		refs.current[number] = item;
		refs.current[number].cache(default_cache_props);
		refs.current['falling_items'].add(item);

		refs.current[number].to({
			y:size.current + item_size.h,
			rotation:(rotate_on_fall) ? (600 * Math.random() - 300) : null,
			duration:speed_multiplier + 0.5 * speed_multiplier * Math.random(),
			onFinish:() => {
				if(animations.current === false) return;

				const number_int = parseInt(number.replace("item_", ""));
				const auto_create_new = ((number_int % items_generation_multiplier) == 0) ? false : true;
				if(auto_create_new) {
					setTimeout(() => {
						if(animations.current === false) return;

						const new_type = setItemType();

						const items_in_type = [...Array(indexed_items[new_type].length).keys()];
						const new_index = items_in_type[Math.floor(Math.random() * items_in_type.length)];

						addFallingItem(new_type, new_index);
						delay_next.current = Math.max(delay_next.current * speed_up_factor, min_delay_between_items);
					}, (delay_next.current * 1000));
				}

				if(refs.current[number].attrs?.touched ?? false) {
					return;
				}

				refs.current[number].destroy();
				if(['ok', 'superok'].includes(refs.current[number].attrs.type)) updateScore('miss');
			},
		});
	};

	const detectCollisions = () => {
		if(refs.current['falling_items'] == undefined || animations.current === false) return;

		if(refs.current['falling_items'].getChildren().length == 0) {
			if(detector.current !== false) requestAnimationFrame(detectCollisions);
			return;
		}
		refs.current['falling_items'].getChildren().map((item) => {
			const type = item.attrs.type;
			if(type == undefined) return;

			if(animations.current === false) return;
			if(item.y() < refs.current['limit'].y()) return;
			if(is_debug) item.opacity(1);

			if(haveIntersection(item.getClientRect(), refs.current['player_doomy'].getClientRect())) {
				const item_x = item.x();

				item.destroy();

				item.setAttrs({ touched:true });
				updateScore(type, item_x);

				if(['ok', 'superok'].includes(type)) {
					(type == 'ok') ? soundOK() : soundSuperOK();
					return;
				}

				(type == 'ko') ? soundKO() : soundSuperKO();
			}
		});

		if(detector.current !== false) requestAnimationFrame(detectCollisions);
	};

	const movePlayer = (x) => {
		if(refs.current['player'] == undefined) return;

		const pos = {
			x:Math.max(player_size.w / 2, Math.min(size.current - player_size.w / 2, x)),
			y:size.current,
		};

		refs.current['player'].setAttrs(pos);
	};

	const onMouseMove = (e) => {
		movePlayer(e.evt.offsetX);
	};

	const onDragMove = (e) => {
		movePlayer(e.target.attrs.x);
	};

	if(!full_loaded) {
		return (
			<div class="canvas_cont">
				<Spinner>Loading game</Spinner>
			</div>
		)
	}

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	let full_bg_scale = 1;
	if(isNotEmpty((indexed_items?.full_bg ?? {}), 'fillPatternImage')) {
		full_bg_scale = size.current / Math.min(indexed_items.full_bg.image_w, indexed_items.full_bg.image_h);
	}

	return (
		<Stage
			ref={(node) => { refs.current['main_stage'] = node; }}
			className={main_classes.join(" ")}
			width={size.current}
			height={size.current}
			onMouseMove={onMouseMove}
		>
			<Layer ref={(node) => { refs.current['layer'] = node; }}>
				<Rect
					width={size.current}
					height={size.current}
					fill={stage?.catch_bg_color ?? 'rgba(230, 230, 230, 1)'}
					fillPatternImage={indexed_items?.full_bg?.fillPatternImage ?? null}
					fillPatternRepeat='no-repeat'
					fillPatternScaleX={full_bg_scale}
					fillPatternScaleY={full_bg_scale}
					fillPriority={(isNotEmpty((indexed_items?.full_bg ?? {}), 'fillPatternImage')) ? 'pattern' : 'color'}
				/>

				<Line
					ref={(node) => { refs.current['limit'] = node; }}
					y={min_detection_y}
					points={[0, 0, size.current, 0]}
					strokeWidth={1}
					stroke='black'
					opacity={(is_debug) ? 0.2 : 0}
				/>

				<Group ref={(node) => { refs.current['falling_items'] = node; }} />

				<Group ref={(node) => { refs.current['score_labels'] = node; }} />

				<Group
					ref={(node) => { refs.current['player'] = node; }}
					x={size.current / 2}
					y={size.current}
					offsetX={player_size.w / 2}
					offsetY={player_size.h}
					draggable={true}
					onDragMove={onDragMove}
					onTap={() => {}}
					width={player_size.w + 100}
					height={player_size.h + 50}
				>
					<Rect
						width={player_size.w + 100}
						height={player_size.h + 50}
						x={-50}
						y={-50}
						strokeWidth={1}
						stroke='red'
						opacity={0}
					/>

					<Rect
						ref={(node) => { refs.current['player_image'] = node; }}
						width={100}
						height={60}
						x={(player_size.w - 100) / 2}
						y={player_size.h - 60}
						fillPatternImage={indexed_items?.full_player?.fillPatternImage ?? null}
						fillPatternRepeat='no-repeat'
						fillPatternScaleX={0.5}
						fillPatternScaleY={0.5}
					/>

					<Rect
						ref={(node) => { refs.current['player_doomy'] = node; }}
						width={0.5 * player_size.w}
						height={5}
						x={0.25 * player_size.w}
						fill='black'
						opacity={0}
					/>
				</Group>
			</Layer>
		</Stage>
	)
});

export default CatchItCanvas;